import { z } from 'zod';
import {
  AlertRuleStatusSchema,
  AlertSeveritySchema,
  IsoTimestampSchema,
  UuidSchema,
} from './common';
import { LogSchema } from './log';

const ScopeAllOrIdsSchema = z.union([z.literal('all'), z.array(UuidSchema)]);

export const ConditionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('any_match') }),
  z.object({
    type: z.literal('threshold'),
    count: z.number().int().min(1).max(10000),
    windowMinutes: z.number().int().min(1).max(1440),
  }),
  z.object({
    type: z.literal('content_match'),
    pattern: z.string().min(1).max(500),
    isRegex: z.boolean(),
  }),
  z.object({
    type: z.literal('metadata_match'),
    key: z.string().min(1).max(128),
    operator: z.enum(['=', '!=', 'contains', '>', '<']),
    value: z.string().max(500),
  }),
]);

// Named Discord webhook targets resolved server-side from env vars.
// alerts -> OWL Alerts, status -> OWL Status, reports -> OWL Reports.
export const DiscordTargetSchema = z.enum(['alerts', 'status', 'reports']);
export type DiscordTarget = z.infer<typeof DiscordTargetSchema>;

export const ChannelsSchema = z.object({
  email: z.array(z.string().email()).default([]),
  webhook: z.string().url().optional(),
  slack: z.string().url().optional(),
  // true -> default webhook (OWL Alerts); a target key -> a named webhook; a URL -> a specific webhook
  discord: z.union([z.boolean(), DiscordTargetSchema, z.string().url()]).optional(),
  // Web Push to every browser that opted in. Unlike the others this has no address
  // of its own: the recipients are whatever subscriptions exist at dispatch time.
  push: z.boolean().optional(),
});

export const AlertRuleSchema = z.object({
  id: UuidSchema,
  name: z.string().min(1).max(128),
  description: z.string().nullable(),
  severity: AlertSeveritySchema,
  scopeApplications: ScopeAllOrIdsSchema,
  scopeMessageTypes: ScopeAllOrIdsSchema,
  condition: ConditionSchema,
  channels: ChannelsSchema,
  cooldownMinutes: z.number().int().min(0).max(1440),
  groupSimilar: z.boolean(),
  status: AlertRuleStatusSchema,
  lastTriggeredAt: IsoTimestampSchema.nullable(),
  triggerCount7d: z.number().int().nonnegative(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
});

export const CreateAlertRuleSchema = z.object({
  name: z.string().min(1).max(128),
  description: z.string().max(500).optional(),
  severity: AlertSeveritySchema,
  scopeApplications: ScopeAllOrIdsSchema,
  scopeMessageTypes: ScopeAllOrIdsSchema,
  condition: ConditionSchema,
  channels: ChannelsSchema,
  cooldownMinutes: z.number().int().min(0).max(1440).default(5),
  groupSimilar: z.boolean().default(true),
  status: AlertRuleStatusSchema.default('active'),
});

export const UpdateAlertRuleSchema = CreateAlertRuleSchema.partial();

export const TestAlertRuleResponseSchema = z.object({
  wouldTriggerCount: z.number().int().nonnegative(),
  sampleMatches: z.array(LogSchema),
});

export type Condition = z.infer<typeof ConditionSchema>;
export type Channels = z.infer<typeof ChannelsSchema>;
export type AlertRule = z.infer<typeof AlertRuleSchema>;
export type CreateAlertRule = z.infer<typeof CreateAlertRuleSchema>;
export type UpdateAlertRule = z.infer<typeof UpdateAlertRuleSchema>;
export type TestAlertRuleResponse = z.infer<typeof TestAlertRuleResponseSchema>;
